import { useState, useEffect } from "react";
import { postData } from "../../../../Services/Fetchnodeservices";
import { useLocation, useNavigate } from "react-router-dom";

// Icons
const SpinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const OrderStatusBadge = ({ status, theme }) => {
  const statusMap = {
    delivered: { bg: "rgba(16,185,129,0.12)", color: "#10b981", text: "Delivered" },
    completed: { bg: "rgba(16,185,129,0.12)", color: "#10b981", text: "Completed" },
    pending: { bg: "rgba(245,158,11,0.12)", color: "#f59e0b", text: "Pending" },
    placed: { bg: "rgba(59,130,246,0.12)", color: "#3b82f6", text: "Placed" },
    accepted: { bg: "rgba(139,92,246,0.12)", color: "#8b5cf6", text: "Accepted" },
    processing: { bg: "rgba(59,130,246,0.12)", color: "#3b82f6", text: "Processing" },
    cancelled: { bg: "rgba(239,68,68,0.12)", color: "#ef4444", text: "Cancelled" },
    confirmed: { bg: "rgba(139,92,246,0.12)", color: "#8b5cf6", text: "Confirmed" },
    shipped: { bg: "rgba(6,182,212,0.12)", color: "#06b6d4", text: "Shipped" }
  };
  const c = statusMap[status?.toLowerCase()] || { bg: "rgba(107,114,128,0.12)", color: "#6b7280", text: status || "Unknown" };
  return (
    <span style={{ fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "12px", background: c.bg, color: c.color }}>
      {c.text}
    </span>
  );
};

const RestaurantOrderHistory = ({ onClose, isDark = true, restaurantId, restaurantName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get designation_id from multiple sources for persistence
  const getDesignationId = () => {
    // 1. Check location state first
    if (location.state?.designation_id) {
      return location.state.designation_id;
    }
    
    // 2. Check props
    if (restaurantId) {
      return restaurantId;
    }
    
    // 3. Check sessionStorage (from previous navigation)
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.designation_id;
      } catch (e) {
        console.error("Error parsing stored data:", e);
      }
    }
    
    // 4. Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlId = urlParams.get('designation_id');
    if (urlId) {
      return urlId;
    }
    
    return null;
  };
  
  const getRestaurantNameFromSources = () => {
    // 1. Check location state
    if (location.state?.restaurantName) {
      return location.state.restaurantName;
    }
    
    // 2. Check props
    if (restaurantName) {
      return restaurantName;
    }
    
    // 3. Check sessionStorage
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.restaurantName;
      } catch (e) {
        console.error("Error parsing stored data:", e);
      }
    }
    
    // 4. Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlName = urlParams.get('restaurant_name');
    if (urlName) {
      return decodeURIComponent(urlName);
    }
    
    return null;
  };
  
  const designation_id = getDesignationId();
  const passedRestaurantName = getRestaurantNameFromSources();
  
  // Store in sessionStorage for persistence
  useEffect(() => {
    if (designation_id) {
      sessionStorage.setItem('selectedRestaurant', JSON.stringify({
        designation_id: designation_id,
        restaurantName: passedRestaurantName
      }));
    }
  }, [designation_id, passedRestaurantName]);
  
  const theme = isDark ? 'dark' : 'light';
  
  const colors = {
    background: isDark ? "#0d1117" : "#f9fafb",
    surface: isDark ? "#141824" : "#ffffff",
    border: isDark ? "#1e2740" : "#e5e7eb",
    borderLight: isDark ? "#1e2740" : "#f3f4f6",
    textPrimary: isDark ? "#f1f5f9" : "#111827",
    textSecondary: isDark ? "#94a3b8" : "#6b7280",
    textMuted: isDark ? "#64748b" : "#9ca3af",
    inputBg: isDark ? "#141824" : "#f9fafb",
  };

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [riderDetails, setRiderDetails] = useState({});
  const [restaurantInfo, setRestaurantInfo] = useState({ name: passedRestaurantName, id: designation_id });
  const [fetchingDetails, setFetchingDetails] = useState(false);

  // Check if we have designation_id, if not show error
  useEffect(() => {
    if (!designation_id) {
      setError("No restaurant selected. Please go back and select a restaurant.");
      setLoading(false);
    } else {
      fetchOrders();
      fetchRestaurantInfo();
    }
  }, [designation_id]);

  const fetchRestaurantInfo = async () => {
    try {
      const response = await postData("resturant/ordersbydesignation_id", {
        designation_id: designation_id
      });
      if (response?.status && response?.data) {
        setRestaurantInfo({
          name: response.data.organisation_name || response.data.name || passedRestaurantName,
          id: designation_id
        });
      }
    } catch (err) {
      console.error("Error fetching restaurant info:", err);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    
    if (!designation_id) {
      setError("No restaurant ID available");
      setLoading(false);
      return;
    }
    
    console.log("Fetching orders for designation_id:", designation_id);
    
    try {
      const response = await postData("resturant/ordersbydesignation_id", {
        designation_id: designation_id
      });
      console.log("Orders response:", response);
      
      // Extract the orders array from the response
      let ordersData = [];
      if (response?.status === true && Array.isArray(response?.data)) {
        ordersData = response.data;
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response?.orders && Array.isArray(response.orders)) {
        ordersData = response.orders;
      }
      
      console.log("Extracted orders data:", ordersData);
      console.log("Total orders count:", ordersData.length);
      
      setAllOrders(ordersData);
      setOrders(ordersData);
      
      // Fetch user and rider details for each order
      if (ordersData.length > 0) {
        await fetchAdditionalDetails(ordersData);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalDetails = async (ordersData) => {
    setFetchingDetails(true);
    const userMap = {};
    const riderMap = {};
    
    for (const order of ordersData) {
      // Fetch user details if user_id exists
      if (order.user_id && !userMap[order.user_id]) {
        try {
          const userResponse = await postData("users/getuserbyid", {
            userid: order.user_id
          });
          
          if (userResponse?.status && userResponse?.data) {
            userMap[order.user_id] = userResponse.data;
          } else if (userResponse?.data) {
            userMap[order.user_id] = userResponse.data;
          }
        } catch (err) {
          console.error("Error fetching user details:", err);
        }
      }
      
      // Fetch rider details if delivery_person_id exists
      if (order.delivery_person_id && !riderMap[order.delivery_person_id]) {
        try {
          const riderResponse = await postData("users/getuserbyid", {
            userid: order.delivery_person_id
          });
          
          if (riderResponse?.status && riderResponse?.data) {
            riderMap[order.delivery_person_id] = riderResponse.data;
          } else if (riderResponse?.data) {
            riderMap[order.delivery_person_id] = riderResponse.data;
          }
        } catch (err) {
          console.error("Error fetching rider details:", err);
        }
      }
    }
    
    setUserDetails(userMap);
    setRiderDetails(riderMap);
    setFetchingDetails(false);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const filterOrders = (type) => {
    setActiveTab(type);
    
    if (type === "all") {
      setOrders(allOrders);
    } else if (type === "delivered") {
      setOrders(allOrders.filter(o => 
        o.order_status_restro?.toLowerCase() === "delivered" || 
        o.order_status_driver?.toLowerCase() === "delivered"
      ));
    } else if (type === "pending") {
      setOrders(allOrders.filter(o => {
        const status = (o.order_status_restro || "").toLowerCase();
        return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
      }));
    } else if (type === "cancelled") {
      setOrders(allOrders.filter(o => 
        o.order_status_restro?.toLowerCase() === "cancelled"
      ));
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getItemsList = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) {
        return items.map(item => `${item.name} x${item.qty}`).join(", ");
      }
      return itemsStr;
    } catch {
      return itemsStr || "-";
    }
  };

  const getItemsArray = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) {
        return items;
      }
      return [];
    } catch {
      return [];
    }
  };

  const getCustomerName = (order) => {
    if (userDetails[order.user_id]?.name) {
      return userDetails[order.user_id].name;
    }
    if (userDetails[order.user_id]?.full_name) {
      return userDetails[order.user_id].full_name;
    }
    if (order.user_name) {
      return order.user_name;
    }
    if (order.customer_name) {
      return order.customer_name;
    }
    if (order.receiver_name) {
      return order.receiver_name;
    }
    return `Customer ${order.user_id || "N/A"}`;
  };

  // Calculate statistics
  const stats = {
    total: allOrders.length,
    delivered: allOrders.filter(o => 
      o.order_status_restro?.toLowerCase() === "delivered"
    ).length,
    pending: allOrders.filter(o => {
      const status = (o.order_status_restro || "").toLowerCase();
      return status === "pending" || status === "processing" || status === "confirmed" || status === "placed" || status === "accepted";
    }).length,
    cancelled: allOrders.filter(o => 
      o.order_status_restro?.toLowerCase() === "cancelled"
    ).length,
    totalRevenue: allOrders.reduce((sum, order) => {
      const amount = parseFloat(order.final_amount) || parseFloat(order.amount) || 0;
      return sum + amount;
    }, 0)
  };

  const ViewOrderModal = ({ order, onClose }) => {
    const user = userDetails[order.user_id];
    const rider = riderDetails[order.delivery_person_id];
    
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }} onClick={onClose}>
        <div style={{
          background: colors.surface,
          borderRadius: "16px",
          maxWidth: "750px",
          width: "100%",
          maxHeight: "85vh",
          overflow: "auto",
          border: `1px solid ${colors.border}`
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: "20px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            background: colors.surface,
            zIndex: 1
          }}>
            <h3 style={{ margin: 0, color: colors.textPrimary }}>Order Details #{order.id}</h3>
            <button onClick={onClose} style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: colors.textMuted
            }}>×</button>
          </div>
          <div style={{ padding: "20px" }}>
            {/* Restaurant Name Highlighted */}
            <div style={{ 
              background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)",
              padding: "16px",
              borderRadius: "12px",
              marginBottom: "20px",
              border: `1px solid ${colors.border}`,
              textAlign: "center"
            }}>
              <strong style={{ color: "#3b82f6", fontSize: "18px" }}>🏪 {restaurantInfo.name || order.restaurant_name || "Restaurant"}</strong>
            </div>

            {/* User & Rider Info */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
              gap: "16px", 
              marginBottom: "20px",
              background: colors.inputBg,
              padding: "16px",
              borderRadius: "12px"
            }}>
              <div>
                <strong style={{ color: colors.textPrimary }}>👤 Customer:</strong>
                <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>
                  {user?.name || user?.full_name || order.user_name || order.customer_name || order.receiver_name || "N/A"}
                </p>
                {user?.contact && (
                  <p style={{ margin: "4px 0 0", fontSize: "11px", color: colors.textMuted }}>{user.contact}</p>
                )}
              </div>
              <div>
                <strong style={{ color: colors.textPrimary }}>🛵 Rider:</strong>
                {rider ? (
                  <>
                    <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{rider.name || rider.full_name || "N/A"}</p>
                    {rider.contact && (
                      <p style={{ margin: "4px 0 0", fontSize: "11px", color: colors.textMuted }}>{rider.contact}</p>
                    )}
                  </>
                ) : (
                  <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{order.rider_name || "Not assigned yet"}</p>
                )}
              </div>
            </div>

            {/* Order Details */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
              gap: "16px", 
              marginBottom: "20px",
              background: colors.inputBg,
              padding: "16px",
              borderRadius: "12px"
            }}>
              <div>
                <strong style={{ color: colors.textPrimary }}>Order ID:</strong>
                <p style={{ margin: "4px 0 0", color: colors.textSecondary, fontFamily: "monospace" }}>#{order.id}</p>
              </div>
              <div>
                <strong style={{ color: colors.textPrimary }}>Date & Time:</strong>
                <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{formatDateTime(order.created_at)}</p>
              </div>
              <div>
                <strong style={{ color: colors.textPrimary }}>Payment Method:</strong>
                <p style={{ margin: "4px 0 0", color: colors.textSecondary }}>{order.pay_method || "N/A"}</p>
              </div>
              <div>
                <strong style={{ color: colors.textPrimary }}>Payment Status:</strong>
                <p style={{ 
                  margin: "4px 0 0", 
                  color: order.pay_status === "paid" ? "#10b981" : "#f59e0b",
                  fontWeight: 600
                }}>
                  {order.pay_status === "paid" ? "✓ Paid" : "Pending"}
                </p>
              </div>
            </div>

            {/* Items Details */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: colors.textPrimary }}>Items Ordered:</strong>
              <div style={{ 
                marginTop: "8px", 
                background: colors.inputBg, 
                padding: "12px", 
                borderRadius: "8px",
                border: `1px solid ${colors.border}`
              }}>
                {getItemsArray(order.items).map((item, idx) => (
                  <div key={idx} style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    marginBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0,
                    paddingBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0,
                    borderBottom: idx < getItemsArray(order.items).length - 1 ? `1px solid ${colors.border}` : "none"
                  }}>
                    <div>
                      <span style={{ color: colors.textPrimary, fontWeight: 500 }}>{item.name}</span>
                      {item.variant_name && (
                        <span style={{ color: colors.textMuted, fontSize: "11px", marginLeft: "8px" }}>({item.variant_name})</span>
                      )}
                      <div style={{ color: colors.textMuted, fontSize: "11px", marginTop: "4px" }}>
                        Qty: {item.qty} × ₹{item.price}
                      </div>
                    </div>
                    <span style={{ color: colors.textPrimary, fontWeight: 600 }}>₹{item.price * item.qty}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Breakdown */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: colors.textPrimary }}>Price Breakdown:</strong>
              <div style={{ marginTop: "8px", background: colors.inputBg, padding: "16px", borderRadius: "8px", border: `1px solid ${colors.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Item Total:</span>
                  <span style={{ color: colors.textPrimary }}>₹{order.price || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Delivery Charge:</span>
                  <span style={{ color: colors.textPrimary }}>₹{order.delivery_charge || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Tax:</span>
                  <span style={{ color: colors.textPrimary }}>₹{order.tax || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Platform Fee:</span>
                  <span style={{ color: colors.textPrimary }}>₹{order.user_platform_fee || order.business_platform_fee || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: colors.textSecondary }}>Handling Charge:</span>
                  <span style={{ color: colors.textPrimary }}>₹{order.handling_charge || 0}</span>
                </div>
                {order.discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ color: colors.textSecondary }}>Discount:</span>
                    <span style={{ color: "#10b981" }}>-₹{order.discount}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "12px", marginTop: "8px", borderTop: `1px solid ${colors.border}` }}>
                  <strong style={{ color: colors.textPrimary, fontSize: "16px" }}>Total Amount:</strong>
                  <strong style={{ color: "#10b981", fontSize: "18px" }}>₹{order.final_amount || order.amount || 0}</strong>
                </div>
              </div>
            </div>

            {/* Order Timeline */}
            <div>
              <strong style={{ color: colors.textPrimary }}>Order Timeline:</strong>
              <div style={{ marginTop: "8px", background: colors.inputBg, padding: "16px", borderRadius: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ color: colors.textMuted }}>📅 Order Placed</span>
                  <span style={{ color: colors.textSecondary }}>{formatDateTime(order.created_at)}</span>
                </div>
                {order.restro_accept_time && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textMuted }}>✅ Restaurant Accepted</span>
                    <span style={{ color: colors.textSecondary }}>{formatDateTime(order.restro_accept_time)}</span>
                  </div>
                )}
                {order.restro_ready_time && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textMuted }}>🍽️ Restaurant Ready</span>
                    <span style={{ color: colors.textSecondary }}>{formatDateTime(order.restro_ready_time)}</span>
                  </div>
                )}
                {order.rider_pickup_time && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textMuted }}>🛵 Rider Pickup</span>
                    <span style={{ color: colors.textSecondary }}>{formatDateTime(order.rider_pickup_time)}</span>
                  </div>
                )}
                {order.order_delivered_time && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: colors.textMuted }}>🎯 Delivered</span>
                    <span style={{ color: "#10b981", fontWeight: 600 }}>{formatDateTime(order.order_delivered_time)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: colors.background, 
      fontFamily: "'DM Sans', sans-serif",
      padding: "20px 24px"
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .order-container {
          animation: slideIn 0.3s ease;
        }
      `}</style>

      <div className="order-container">
        {/* Enhanced Header with Restaurant Info */}
        <div style={{ 
          background: `linear-gradient(135deg, ${colors.surface} 0%, ${isDark ? '#0f1520' : '#f1f5f9'} 100%)`,
          borderRadius: "16px",
          padding: "24px",
          marginBottom: "24px",
          border: `1px solid ${colors.border}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button 
                onClick={handleBack}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "10px",
                  cursor: "pointer",
                  color: colors.textSecondary,
                  fontSize: "13px",
                  fontWeight: 500,
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "#1e2740" : "#e2e8f0"}
                onMouseLeave={(e) => e.currentTarget.style.background = colors.surface}
              >
                <ChevronLeftIcon />
                Back
              </button>
              
              <div>
                <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: colors.textPrimary }}>
                  🍽️ Order History
                </h1>
                <div style={{ marginTop: "8px" }}>
                  <span style={{ 
                    background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                    color: "white",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "inline-block"
                  }}>
                    {restaurantInfo.name || passedRestaurantName || "Restaurant"}
                  </span>
                  <span style={{ 
                    marginLeft: "12px",
                    fontSize: "12px",
                    color: colors.textMuted,
                    fontFamily: "monospace"
                  }}>
                    ID: {designation_id}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ 
              display: "flex", 
              gap: "12px",
              background: colors.inputBg,
              padding: "12px 20px",
              borderRadius: "12px",
              border: `1px solid ${colors.border}`
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "24px", fontWeight: 800, color: "#3b82f6" }}>{stats.total}</div>
                <div style={{ fontSize: "11px", color: colors.textMuted }}>Total Orders</div>
              </div>
              <div style={{ width: "1px", background: colors.border }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "24px", fontWeight: 800, color: "#10b981" }}>₹{stats.totalRevenue.toLocaleString()}</div>
                <div style={{ fontSize: "11px", color: colors.textMuted }}>Revenue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div style={{
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: "13px", color: "#fca5a5" }}>⚠ {error}</span>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: "4px 12px",
                borderRadius: "6px",
                border: "none",
                background: colors.surface,
                color: colors.textPrimary,
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* No Designation ID Error */}
        {!designation_id && !error && (
          <div style={{
            textAlign: "center",
            padding: "80px 20px",
            background: colors.surface,
            borderRadius: "16px",
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ fontSize: "80px", marginBottom: "20px" }}>⚠️</div>
            <h3 style={{ margin: "0 0 12px", color: colors.textPrimary, fontSize: "20px" }}>No Restaurant Selected</h3>
            <p style={{ margin: 0, fontSize: "14px", color: colors.textMuted }}>
              Please go back and select a restaurant to view orders
            </p>
            <button
              onClick={handleBack}
              style={{
                marginTop: "20px",
                padding: "10px 24px",
                background: "#3b82f6",
                border: "none",
                borderRadius: "8px",
                color: "#fff",
                cursor: "pointer",
                fontSize: "14px"
              }}
            >
              Go Back
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && designation_id && (
          <div style={{ 
            display: "flex", 
            flexDirection: "column",
            alignItems: "center", 
            justifyContent: "center", 
            padding: "80px 20px",
            background: colors.surface,
            borderRadius: "16px",
            border: `1px solid ${colors.border}`
          }}>
            <SpinnerIcon />
            <p style={{ marginTop: "16px", color: colors.textMuted }}>Loading orders...</p>
          </div>
        )}

        {/* Orders Content */}
        {!loading && !error && designation_id && (
          stats.total > 0 ? (
            <>
              {/* Stats Cards Row */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "24px"
              }}>
                <div style={{
                  background: colors.surface,
                  borderRadius: "12px",
                  padding: "16px",
                  border: `1px solid ${colors.border}`,
                  textAlign: "center",
                  transition: "transform 0.2s"
                }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#3b82f6" }}>{stats.total}</div>
                  <div style={{ fontSize: "12px", color: colors.textMuted }}>Total Orders</div>
                </div>
                <div style={{
                  background: colors.surface,
                  borderRadius: "12px",
                  padding: "16px",
                  border: `1px solid ${colors.border}`,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#10b981" }}>{stats.delivered}</div>
                  <div style={{ fontSize: "12px", color: colors.textMuted }}>Delivered</div>
                </div>
                <div style={{
                  background: colors.surface,
                  borderRadius: "12px",
                  padding: "16px",
                  border: `1px solid ${colors.border}`,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#f59e0b" }}>{stats.pending}</div>
                  <div style={{ fontSize: "12px", color: colors.textMuted }}>Pending</div>
                </div>
                <div style={{
                  background: colors.surface,
                  borderRadius: "12px",
                  padding: "16px",
                  border: `1px solid ${colors.border}`,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#ef4444" }}>{stats.cancelled}</div>
                  <div style={{ fontSize: "12px", color: colors.textMuted }}>Cancelled</div>
                </div>
              </div>

              {/* Filter Tabs */}
              <div style={{
                display: "flex",
                gap: "8px",
                marginBottom: "20px",
                flexWrap: "wrap",
                borderBottom: `1px solid ${colors.border}`,
                paddingBottom: "12px"
              }}>
                {[
                  { key: "all", label: "All Orders", count: stats.total, color: "#3b82f6" },
                  { key: "delivered", label: "Delivered", count: stats.delivered, color: "#10b981" },
                  { key: "pending", label: "Pending", count: stats.pending, color: "#f59e0b" },
                  { key: "cancelled", label: "Cancelled", count: stats.cancelled, color: "#ef4444" }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => filterOrders(tab.key)}
                    style={{
                      background: activeTab === tab.key ? tab.color : "transparent",
                      color: activeTab === tab.key ? "#fff" : colors.textSecondary,
                      border: activeTab === tab.key ? "none" : `1px solid ${colors.border}`,
                      padding: "8px 20px",
                      borderRadius: "24px",
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              {/* Orders Table */}
              <div style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: "12px",
                overflow: "hidden"
              }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
                    <thead>
                      <tr style={{ background: isDark ? "#0f1520" : "#f9fafb" }}>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Order ID</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Date & Time</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Customer</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Items</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Amount</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Payment</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Status</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: isDark ? "#3b82f6" : "#4f46e5", borderBottom: `1px solid ${colors.border}` }}>Action</th>
                       </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, idx) => (
                        <tr
                          key={order.id || idx}
                          style={{
                            borderBottom: idx < orders.length - 1 ? `1px solid ${colors.borderLight}` : "none",
                            transition: "background 0.2s ease"
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)")
                          }
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: colors.textPrimary, fontFamily: "monospace" }}>
                            #{order.id}
                           </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textMuted }}>
                            {formatDateTime(order.created_at)}
                           </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <div style={{
                                width: "28px", height: "28px", borderRadius: "50%",
                                background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "12px", fontWeight: 600, color: "#fff"
                              }}>
                                {getCustomerName(order).charAt(0).toUpperCase()}
                              </div>
                              <strong>{getCustomerName(order)}</strong>
                            </div>
                           </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            {getItemsList(order.items)}
                           </td>
                          <td style={{ padding: "12px 14px", fontSize: "14px", fontWeight: 700, color: colors.textPrimary }}>
                            ₹{order.final_amount || order.amount || 0}
                           </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: colors.textSecondary }}>
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              background: order.pay_method === "COD" ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)",
                              color: order.pay_method === "COD" ? "#f59e0b" : "#10b981"
                            }}>
                              {order.pay_method === "COD" ? "💵 COD" : "💳 Online"}
                            </span>
                           </td>
                          <td style={{ padding: "12px 14px" }}>
                            <OrderStatusBadge status={order.order_status_restro || order.status} theme={theme} />
                           </td>
                          <td style={{ padding: "12px 14px" }}>
                            <button
                              onClick={() => setSelectedOrder(order)}
                              style={{
                                background: "rgba(59,130,246,0.1)",
                                border: "1px solid rgba(59,130,246,0.3)",
                                cursor: "pointer",
                                color: "#3b82f6",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "12px",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                transition: "all 0.2s"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#3b82f6";
                                e.currentTarget.style.color = "#fff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(59,130,246,0.1)";
                                e.currentTarget.style.color = "#3b82f6";
                              }}
                            >
                              <EyeIcon /> View Details
                            </button>
                           </td>
                         </tr>
                      ))}
                    </tbody>
                   </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              background: colors.surface,
              borderRadius: "16px",
              border: `1px solid ${colors.border}`
            }}>
              <div style={{ fontSize: "80px", marginBottom: "20px" }}>🏪</div>
              <h3 style={{ margin: "0 0 12px", color: colors.textPrimary, fontSize: "20px" }}>No Orders Found</h3>
              <p style={{ margin: 0, fontSize: "14px", color: colors.textMuted }}>
                This restaurant hasn't received any orders yet
              </p>
            </div>
          )
        )}
      </div>

      {/* View Order Modal */}
      {selectedOrder && (
        <ViewOrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
};

export default RestaurantOrderHistory;